import React, { useState, useEffect, useMemo, useCallback } from 'react';

const STEPS = (t) => [
  {
    title: 'Welcome to SmartSort!',
    body: `SmartSort helps you place ${t.students.toLowerCase()} into ${t.classes.toLowerCase()} using smart algorithms that respect friendships, separations, and balance.`,
    selector: null,
    position: 'center'
  },
  {
    title: 'Navigation',
    body: 'Use the sidebar to move between sections. Each tab manages a different part of your placement project.',
    selector: '.sidebar-nav',
    position: 'right'
  },
  {
    title: 'Projects',
    body: 'Projects represent a placement session — for example, a specific grade level or summer session. Switch projects from the sidebar.',
    selector: '.workspace-card-button',
    position: 'right'
  },
  {
    title: `Your ${t.students}`,
    body: `Import your ${t.students.toLowerCase()} roster from a spreadsheet or add them manually. Set friend requests and keep-away pairs here.`,
    selector: null,
    position: 'center',
    icon: '👥'
  },
  {
    title: `${t.classes} & Grades`,
    body: `Define the ${t.classes.toLowerCase()} your ${t.students.toLowerCase()} will be placed into. Set capacity limits and grade levels for each.`,
    selector: null,
    position: 'center',
    icon: '📚'
  },
  {
    title: 'The Planner',
    body: `The Planner is your command center. View and manage assignments in a table or drag-and-drop board. Statistics update in real time as you make changes.`,
    selector: null,
    position: 'center',
    icon: '📋'
  },
  {
    title: 'The Solver',
    body: `Click "Generate" to run the placement algorithm. It optimizes for friend requests, separations, gender balance, and capacity. Review the suggestions and apply the ones you like.`,
    selector: null,
    position: 'center',
    icon: '⚡'
  },
  {
    title: 'AI Assistant',
    body: `Have a question? Click the chat bubble to ask the SmartSort AI assistant. It can help you with importing ${t.students.toLowerCase()}, running the solver, understanding factors, and more.`,
    selector: '.chat-bubble',
    position: 'left'
  },
  {
    title: "You're all set!",
    body: `Start by importing your ${t.students.toLowerCase()} roster, defining your ${t.classes.toLowerCase()}, and running your first placement. You can replay this tour anytime from the menu.`,
    selector: null,
    position: 'center',
    icon: '🎉'
  }
];

export default function TutorialOverlay({ terminology, onComplete }) {
  const [currentStep, setCurrentStep] = useState(0);
  const [spotlightRect, setSpotlightRect] = useState(null);
  const steps = useMemo(() => STEPS(terminology), [terminology]);
  const step = steps[currentStep];
  const isFirst = currentStep === 0;
  const isLast = currentStep === steps.length - 1;

  // Measure spotlight target
  useEffect(() => {
    if (!step.selector) {
      setSpotlightRect(null);
      return;
    }

    function measure() {
      const el = document.querySelector(step.selector);
      if (el) {
        const rect = el.getBoundingClientRect();
        setSpotlightRect({
          top: rect.top - 8,
          left: rect.left - 8,
          width: rect.width + 16,
          height: rect.height + 16
        });
      } else {
        setSpotlightRect(null);
      }
    }

    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [step.selector, currentStep]);

  const handleNext = useCallback(() => {
    if (isLast) {
      onComplete();
    } else {
      setCurrentStep(s => s + 1);
    }
  }, [isLast, onComplete]);

  // Keyboard navigation
  useEffect(() => {
    function handleKey(e) {
      if (e.key === 'Escape') onComplete();
      if (e.key === 'ArrowRight' || e.key === 'Enter') handleNext();
      if (e.key === 'ArrowLeft' && !isFirst) setCurrentStep(s => s - 1);
    }
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [currentStep, isFirst, isLast, handleNext, onComplete]);

  // Position card near spotlight
  function getCardStyle() {
    if (!spotlightRect || step.position === 'center') return undefined;

    const cardWidth = 400;
    const gap = 20;

    if (step.position === 'right') {
      return {
        top: Math.max(20, spotlightRect.top),
        left: Math.min(spotlightRect.left + spotlightRect.width + gap, window.innerWidth - cardWidth - 20)
      };
    }

    if (step.position === 'bottom') {
      return {
        top: spotlightRect.top + spotlightRect.height + gap,
        left: Math.max(20, spotlightRect.left)
      };
    }

    if (step.position === 'left') {
      const cardHeight = 300;
      return {
        top: Math.max(20, spotlightRect.top - cardHeight - gap),
        left: Math.max(20, spotlightRect.left - cardWidth + spotlightRect.width - 20)
      };
    }

    return undefined;
  }

  const cardPositionStyle = getCardStyle();
  const isCentered = !cardPositionStyle;

  return (
    <div className="tutorial-overlay">
      {/* Dimmed backdrop - only show when no spotlight */}
      {!spotlightRect && <div className="tutorial-backdrop" />}

      {/* Spotlight with box-shadow trick */}
      {spotlightRect && (
        <div
          className="tutorial-spotlight"
          style={{
            top: spotlightRect.top,
            left: spotlightRect.left,
            width: spotlightRect.width,
            height: spotlightRect.height
          }}
        />
      )}

      {/* Step card */}
      <div
        className={`tutorial-card ${isCentered ? 'tutorial-card-center' : 'tutorial-card-positioned'}`}
        style={cardPositionStyle}
        key={currentStep}
      >
        {/* Step icon for centered steps */}
        {step.icon && isCentered ? (
          <div className="tutorial-card-icon">{step.icon}</div>
        ) : null}

        <div className="tutorial-card-step">
          Step {currentStep + 1} of {steps.length}
        </div>

        <h3 className="tutorial-card-title">{step.title}</h3>
        <p className="tutorial-card-body">{step.body}</p>

        {/* Progress dots */}
        <div className="tutorial-dots">
          {steps.map((_, i) => (
            <span
              key={i}
              className={`tutorial-dot${i === currentStep ? ' is-active' : ''}${i < currentStep ? ' is-done' : ''}`}
            />
          ))}
        </div>

        {/* Navigation */}
        <div className="tutorial-card-actions">
          {!isLast ? (
            <button type="button" className="tutorial-skip-btn" onClick={onComplete}>
              Skip tour
            </button>
          ) : <span />}

          <div className="tutorial-card-nav">
            {!isFirst && (
              <button
                type="button"
                className="tutorial-back-btn"
                onClick={() => setCurrentStep(s => s - 1)}
              >
                Back
              </button>
            )}
            <button type="button" className="tutorial-next-btn" onClick={handleNext}>
              {isLast ? 'Get started' : 'Next'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
